import type { ComponentProps, ReactElement, RefObject } from "react";
import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import classNames from "classnames";
import { ArrowDownIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { useCloseOnExclusiveOverlay } from "@design-system/overlay/lightDismiss";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import styles from "./popover.module.scss";

const Popover = (props: ComponentProps<typeof PopoverPrimitive.Root>) => {
    const actionsRef = useCloseOnExclusiveOverlay();

    return (
        <PopoverPrimitive.Root
            data-slot="popover"
            {...props}
            actionsRef={actionsRef}
        />
    );
};

const PopoverTrigger = ({
    children,
    ...props
}: Omit<ComponentProps<typeof PopoverPrimitive.Trigger>, "render"> & {
    children: ReactElement;
}) => (
    <PopoverPrimitive.Trigger
        data-slot="popover-trigger"
        render={children}
        {...props}
    />
);

/**
 * An icon-only trigger has no visible label, so it must name itself; a
 * labelled one keeps the arrow slot the trigger owns.
 */
type PopoverTriggerBag =
    | (TriggerButtonProps & { iconOnly: true; "aria-label": string })
    | (TriggerButtonProps & { iconOnly?: false });

interface PopoverTriggerButtonProps extends Omit<
    ComponentProps<typeof PopoverPrimitive.Trigger>,
    "render"
> {
    arrow?: boolean;
    buttonProps?: PopoverTriggerBag;
}

const PopoverTriggerButton = ({
    arrow = true,
    buttonProps,
    children,
    ...props
}: PopoverTriggerButtonProps) => (
    <PopoverPrimitive.Trigger
        data-slot="popover-trigger"
        render={
            buttonProps?.iconOnly ? (
                <Button variant="primary" size="md" {...buttonProps}>
                    {children}
                </Button>
            ) : (
                <Button
                    variant="primary"
                    size="md"
                    {...buttonProps}
                    rightIcon={arrow ? <ArrowDownIcon /> : undefined}
                >
                    {children}
                </Button>
            )
        }
        {...props}
    />
);

interface PopoverContentProps extends ComponentProps<
    typeof PopoverPrimitive.Popup
> {
    align?: "start" | "center" | "end";
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
    disableAnchorTracking?: boolean;
    anchor?:
        | Element
        | null
        | RefObject<Element | null>
        | (() => Element | null);
    portalContainer?: HTMLElement;
}

const PopoverContent = ({
    className,
    align = "center",
    side,
    sideOffset = 4,
    disableAnchorTracking = false,
    anchor,
    portalContainer,
    children,
    ...props
}: PopoverContentProps) => (
    <PopoverPrimitive.Portal container={portalContainer}>
        <PopoverPrimitive.Positioner
            className={styles.positioner}
            align={align}
            side={side}
            sideOffset={sideOffset}
            disableAnchorTracking={disableAnchorTracking}
            anchor={anchor}
        >
            <PopoverPrimitive.Popup
                data-slot="popover-content"
                className={classNames(styles.popoverContent, className)}
                {...props}
            >
                {children}
            </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
);

export { Popover, PopoverTrigger, PopoverContent, PopoverTriggerButton };
